import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';
import { toast } from 'sonner';
import { PenTool, Upload, Trash2, Loader2 } from 'lucide-react';

export const TeacherSignatureCard = () => {
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get('/teacher/signature').then((res) => setSignature(res.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return toast.error('Please choose a PNG, JPG or WEBP image');
    if (file.size > 2 * 1024 * 1024) return toast.error('Signature image must be under 2MB');
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await api.post('/teacher/signature', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Signature saved');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload signature');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete('/teacher/signature');
      toast.success('Signature removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove signature');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-8" style={{ borderColor: '#E3E1DB' }} data-testid="teacher-signature-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Spectral, serif', color: '#163329' }}>
          <PenTool className="h-5 w-5" /> My Signature
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm" style={{ color: '#5C635A' }}>
          Printed with your name and surname on the certificates and Letters of Model Acquisition for your courses. Stored privately. A PNG with a transparent background works best.
        </p>
        {signature?.has_signature ? (
          <div className="p-4 rounded-lg border bg-white inline-block" style={{ borderColor: '#E3E1DB' }}>
            <img src={signature.signature_url} alt="My signature" className="h-16 object-contain" data-testid="teacher-signature-preview" />
          </div>
        ) : (
          <p className="text-sm italic" style={{ color: '#5C635A' }} data-testid="teacher-no-signature">
            No signature yet. Your documents will show "[Signature on file]".
          </p>
        )}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFile} data-testid="teacher-signature-input" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => inputRef.current?.click()} disabled={busy} style={{ backgroundColor: '#163329' }} data-testid="teacher-upload-signature-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {signature?.has_signature ? 'Replace Signature' : 'Upload Signature'}
          </Button>
          {signature?.has_signature && (
            <Button variant="outline" onClick={remove} disabled={busy} data-testid="teacher-delete-signature-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
